<template>
  <v-container>
    <Loading v-if="!isPageLoaded" />

    <div v-if="isPageLoaded">
      <p class="mt-4 text-h6 font-weight-bold" id="personal-info-title">Student's Personal Information</p>

      <!-- Rewards and Achievements -->
      <v-row>
        <v-col cols="12" md="3" class="order-2 order-md-1">
          <v-divider class="mb-2" color="#0D47A1"></v-divider>
          <p class="text-subtitle-2 d-flex align-start">
            <v-icon small color="#0D47A1" class="mr-2">mdi-gift</v-icon> Rewards
          </p>

          <v-row dense v-if="myRewards.length !== 0">
            <v-col cols="12" v-for="(reward, index) in myRewards" :key="index">
              <v-card outlined flat class="d-flex py-2">
                <v-img id="rewardPhoto" :src="require('../../assets/rewards/' + reward.image + '.png')" contain
                  max-width="50" max-height="50" class="ml-2 mr-2"></v-img>

                <div style="width: 100%;" class="d-flex flex-column justify-space-between">
                  <p class="text-subtitle-2 font-weight-bold mb-0">{{ reward.name }}</p>
                  <v-card-text class="pa-0 d-flex align-center ma-0">
                    <p class="text-caption ma-0">{{ reward.points }} points</p>
                    <!-- <v-btn x-small outlined rounded class="red--text my-0 mr-2 ml-auto">Delete</v-btn> -->
                    <!-- <v-btn x-small outlined rounded class="green--text my-0 mr-2 ml-auto">Redeem</v-btn> -->
                  </v-card-text>
                </div>
              </v-card>
            </v-col>
          </v-row>

          <v-row v-else>
            <v-col cols="12">
              <v-card flat id="no-reward">
                <v-card-text class="font-italic text-caption">
                  Collect points and claim rewards to get an edge in your class!!
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <v-divider class="mt-10 mb-2" color="#0D47A1"></v-divider>
          <p class="text-subtitle-2 d-flex align-start">
            <v-icon small color="#0D47A1" class="mr-2">mdi-trophy-variant</v-icon> Achievements
          </p>

          <v-row v-if="myBadges" class="mb-4">
            <v-col cols="3" sm="2" md="3" v-for="(badge, index) in myBadges" :key="index">
              <v-card flat>
                <v-img :src="require('../../assets/badges/' + badge + '.png')"></v-img>
              </v-card>
            </v-col>
          </v-row>

          <v-row v-else>
            <v-col cols="12">
              <v-card flat id="no-achievement">
                <v-card-text class="font-italic text-caption">
                  Keep your attendance streak up and make the next badge yours!!
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-col>

        <!-- Attendance table -->
        <v-col cols="12" md="6" class="order-3 order-md-2">
          <v-card flat>
            <Table :headers="tableHeaders" :items="tableItems" :isPrimary="false" :isSecondary="false" />
          </v-card>
        </v-col>

        <!-- Displays the personal informations of a student -->
        <v-col cols="12" md="3" class="order-1 order-md-3">
          <v-card flat color="#0D47A1" class="d-flex pa-2" v-if="this.$route.params.reference">
            <v-img src="../../assets/eclipse.png" contain max-height="75" max-width="75"></v-img>
            <v-card-text class="pa-0 ma-auto white--text">
              <v-card-title class="py-0 ma-auto font-weight-bold">{{ student !== null ? student.lastName.toUpperCase() :
                ''
              }}</v-card-title>
              <v-card-subtitle class="py-0 ma-auto">{{ student !== null ? student.firstName : '' }} {{ student !== null ?
                student.middleName : '' }}</v-card-subtitle>
            </v-card-text>
          </v-card>

          <v-card flat class="mt-2" v-if="this.$route.params.reference">
            <v-card-text>
              <p><span class="font-weight-bold">GENDER: </span>{{ student !== null ? student.gender.toUpperCase() : '' }}
              </p>
              <p><span class="font-weight-bold">AGE: </span>{{ student !== null ? student.age : '' }}</p>
              <p><span class="font-weight-bold">GUARDIAN: </span>{{ student !== null ? student.guardian.toUpperCase() : ''
              }}</p>
              <p><span class="font-weight-bold">GUARDIAN'S NO.: </span>{{ student !== null ? student.guardianContact : ''
              }}
              </p>
              <p><span class="font-weight-bold">API KEY: </span>{{ student !== null ? student.apiKey : '' }}</p>
              <p><span class="font-weight-bold">REFERENCE NO.: </span>{{ student !== null ? student.reference : '' }}</p>
              <p><span class="font-weight-bold">REWARD POINTS: </span>{{ student !== null ? student.points : '' }}</p>
            </v-card-text>
          </v-card>

          <!-- Opens a dialog/form for editing the student's personal information -->
          <v-dialog v-model="dialogs.update" max-width="500" max-height="500" persistent>
            <template v-slot:activator="{ on, attrs }">
              <v-btn block class="mt-2 white--text" color="#0D47A1" v-on="on">
                <v-icon class="mr-2">mdi-pencil</v-icon>
                EDIT DETAILS
              </v-btn>
            </template>

            <v-card class="pa-4">
              <v-card-title><span class="mx-auto" style="color: #0D47A1;">EDIT STUDENT DETAILS</span></v-card-title>

              <v-text-field outlined label="FIRST NAME" v-model="editDetailsForm.firstName"></v-text-field>
              <v-text-field outlined label="MIDDLE NAME" v-model="editDetailsForm.middleName"></v-text-field>
              <v-text-field outlined label="LAST NAME" v-model="editDetailsForm.lastName"></v-text-field>
              <v-select outlined :items="genderOptions" label="GENDER" v-model="editDetailsForm.gender"></v-select>
              <v-text-field outlined label="AGE" v-model="editDetailsForm.age"></v-text-field>
              <v-text-field outlined label="GUARDIAN" v-model="editDetailsForm.guardian"></v-text-field>
              <v-text-field outlined label="GUARDIAN'S CONTACT NO."
                v-model="editDetailsForm.guardianContact"></v-text-field>
              <v-text-field outlined label="API KEY" v-model="editDetailsForm.apiKey"></v-text-field>

              <v-btn block class="mt-2 white--text" color="#0D47A1" @click="updateDetails()">
                <v-icon class="mr-2">mdi-plus-box</v-icon>
                UPDATE DETAILS
              </v-btn>
              <v-btn block outlined class="mt-2" color="red" @click="dialogs.update = false">
                <v-icon class="mr-2">mdi-close</v-icon>
                CLOSE
              </v-btn>
            </v-card>
          </v-dialog>

          <v-snackbar v-model="snackbar.isSnackbar" :timeout="snackbar.timeout" top color="success">
            {{ snackbar.text }}
          </v-snackbar>

          <v-btn block class="mt-2 white--text" color="#1976D2" @click="generateQrCode()" :loading="loading">
            <v-icon class="mr-2">mdi-download</v-icon>
            GENERATE QR CODE
          </v-btn>
          <v-btn block class="mt-2 white--text" color="#2196F3">
            <v-icon class="mr-2">mdi-send</v-icon>
            TEST CONNECTION
          </v-btn>

          <!-- Opens a dialog with a warning when trying to remove a student -->
          <v-dialog v-model="dialogs.remove" max-width="500" max-height="500" persistent>
            <template v-slot:activator="{ on, attrs }">
              <v-btn block outlined class="mt-2" color="red" v-on="on">
                <v-icon class="mr-2">mdi-delete</v-icon>
                REMOVE STUDENT
              </v-btn>
            </template>

            <v-card class="pa-4">
              <v-card-title><span class="mx-auto" style="color: red;">WARNING</span></v-card-title>
              <v-card-text class="text-center">
                Are you sure you want to remove this student? All recorded attendances of this student will remain.
              </v-card-text>

              <v-btn block class="mt-2 white--text" color="red" @click="removeStudent()">
                <v-icon class="mr-2">mdi-delete</v-icon>
                DELETE ANYWAY
              </v-btn>
              <v-btn block outlined class="mt-2" color="red" @click="dialogs.remove = false">
                <v-icon class="mr-2">mdi-close</v-icon>
                CLOSE
              </v-btn>
            </v-card>
          </v-dialog>
        </v-col>
      </v-row>
    </div>
  </v-container>
</template>

<script>
import Table from '../../components/Table.vue'
import { mapActions, mapState } from 'vuex'
import Loading from '../../components/Loading.vue'

export default {
  name: 'StudentDetails',

  components: {
    Table,
    Loading
  },

  data: () => ({
    isPageLoaded: false,
    loading: false,
    dialogs: {
      update: false,
      award: true,
      remove: false
    },

    genderOptions: ['Male', 'Female'],

    snackbar: {
      isSnackbar: false,
      text: 'Details updated successfully.',
      timeout: 3000
    },

    editDetailsForm: {
      reference: false,
      firstName: null,
      middleName: null,
      lastName: null,
      gender: null,
      age: null,
      guardian: null,
      guardianContact: null,
      apiKey: null,
      points: null,
      rewards: [],
      badges: [],
      awardNotice: [],
      answeredRiddles: []
    },

    tableHeaders: [
      {
        text: 'MONTH',
        value: 'month',
        align: 'start'
      },
      {
        text: 'DATE',
        value: 'date'
      },
      {
        text: 'DAY',
        value: 'day'
      },
      {
        text: 'MORNING',
        value: 'morning'
      },
      {
        text: 'AFTERNOON',
        value: 'afternoon'
      }
    ]
  }),

  computed: {
    ...mapState('students', {
      student: state => state.singleStudent,
    }),

    ...mapState('attendance', {
      attendance: state => state.singleAttendance,
      attendances: state => state.allAttendances,
    }),

    ...mapState('gamify', {
      allRewards: state => state.allRewards,
      allAchievements: state => state.allAchievements
    }),

    tableItems() {
      let singleStudentAttendance = []

      this.attendances.forEach(attendance => {
        if (attendance.reference.toString() === this.student.reference.toString()) {
          console.log('equal');
          let dateArray = attendance.date.split('-')
          let data = new Date(dateArray[0], dateArray[1] - 1, dateArray[2])
          let month = data.toLocaleString('default', { month: 'long' })
          let weekday = data.toLocaleString('default', { weekday: 'long' })

          singleStudentAttendance.push({
            month: month,
            date: dateArray[2],
            day: weekday,
            morning: attendance.morning,
            afternoon: attendance.afternoon
          })
        }
      })
      console.log(singleStudentAttendance)
      return singleStudentAttendance
    },

    myRewards() {
      let rewards = []

      if (this.student) {
        if (this.student.rewards.length !== 0) {
          this.student.rewards.forEach(myReward => {
            this.allRewards.forEach(reward => {
              if (myReward.toString() === reward.id.toString()) {
                rewards.push(reward)
              }
            })
          })
        }
      }
      return rewards
    },

    myAchievements() {
      if (this.allAchievements[0] === undefined || this.allAchievements[0] === null) {
        return false
      } else {
        return this.allAchievements[0]
      }
    },

    myBadges() {
      if (this.student === undefined || this.student === null || this.student.badges.length === 0) {
        console.log('nothingness')
        return false
      } else {
        console.log(this.student)
        return this.student.badges
      }
    },

    noticeOfAward() {
      if (this.student === undefined || this.student === null || this.student.awardNotice.length === 0) {
        console.log('nothingness')
        return false
      } else {
        console.log(this.student)
        return this.student.awardNotice
      }
    }
  },

  methods: {

    ...mapActions('students', ['updateStudentDetails', 'getSingleStudent', 'getSingleAttendance', 'deleteStudent']),
    ...mapActions('attendance', ['getSingleAttendance', 'getAllAttendance']),
    ...mapActions('gamify', ['getAllRewards', 'getAllAchievements', 'emptyAwardNotice']),

    updateDetails: function () {
      this.editDetailsForm.points = this.student.points
      this.editDetailsForm.badges = this.student.badges
      this.updateStudentDetails(this.editDetailsForm)
      this.getSingleStudent(this.$route.params.reference)
      this.dialogs.update = false
      this.snackbar.isSnackbar = true
    },

    generateQrCode: function () {
      this.loading = true
      let color = '0D47A1'
      let bgColor = 'FFFFFF'
      let url = `https://api.qrserver.com/v1/create-qr-code/?data=${this.student.apiKey}&size=500x500&color=${color}&bgcolor=${bgColor}`
      let filename = `${this.student.lastName}_${this.student.firstName}.png`

      fetch(url)
        .then(res => res.blob())
        .then(data => {
          var anchor = document.createElement("a") //creates an anchor tag/element
          anchor.href = window.URL.createObjectURL(data)
          anchor.download = filename; //force download of qr code
          anchor.click()

          window.URL.revokeObjectURL(url) //removes generated link for qr code download
          this.loading = false
        })
        .catch(error => {
          this.loading = false
          console.log(error)
        })

    },

    removeStudent: function () {
      this.deleteStudent(this.student)
      this.dialogs.remove = false
    }
  },

  mounted: async function() {
    this.editDetailsForm = {
      reference: this.$route.params.reference,
      firstName: this.$route.params.firstName,
      lastName: this.$route.params.lastName,
      middleName: this.$route.params.middleName,
      age: this.$route.params.age,
      gender: this.$route.params.gender,
      guardian: this.$route.params.guardian,
      guardianContact: this.$route.params.guardianContact,
      apiKey: this.$route.params.apiKey,
      answeredRiddles: this.$route.params.answeredRiddles,
      awardNotice: [],
      badges: [],
      points: null,
      rewards: [],
    }

    await this.getSingleStudent(this.$route.params.reference)
    await this.getSingleAttendance(this.$route.params.reference)
    await this.getAllRewards()
    await this.getAllAchievements()
    await this.getAllAttendance()

    this.isPageLoaded = true
  }
};

</script>

<style scoped>
#personal-info-title {
  color: #0D47A1;
}

#rewardPhoto {
  border: 1px solid #d9d9d9;
  border-radius: 5px;
  /* background-color: #0D47A1; */
}

#no-reward,
#no-achievement {
  background-color: rgb(242, 241, 255);
}</style>